import type { Disposable } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { RavenService, type RavenService as RavenServiceType } from '@telecom/shared/services/raven-service/raven-service';
import { I18nService, type I18nService as I18nServiceType } from '@telecom/shared/services/i18n';
import type { Config, Driver } from 'driver.js';
import { TRACK_EVENT_TYPE } from '@/constants';
import { TRACK_EVENT_AGENT_REPORT_ACTIONS_SPOTLIGHTED } from '@/constants/track-events';
import {
    OnboardingEligibilityService,
    type OnboardingEligibilityService as OnboardingEligibilityServiceType,
} from '../../common/services/onboarding-eligibility-service';
import {
    ACTION_GUIDE_OVERLAY_COLOR,
    ACTION_GUIDE_OVERLAY_OPACITY,
} from '../../common/constants';
import {
    OnboardingStateService,
    type OnboardingStateService as OnboardingStateServiceType,
} from '../../common/services/onboarding-state-service';
import { createReportActionGuideStep } from '../../common/utils/report-action-guide';
import { registerOnboardingLocaleMessages } from '../../common/utils/onboarding-locale';
import { messages } from '../../common/locales';
import {
    ReportActionGuideBubblePresenter,
    type ReportActionGuideBubblePresenter as ReportActionGuideBubblePresenterType,
} from '../../common/services/report-action-guide-bubble-presenter';
import {
    FtoProOnboardingRuntime,
    type FtoProOnboardingRuntime as FtoProOnboardingRuntimeType,
} from '../fto-pro-onboarding-runtime';
import type { FtoProReportOnboardingService } from '../fto-pro-report-onboarding-service';

/** FTO Pro 报告滚动容器的稳定选择器。 */
const REPORT_CONTENT_SELECTOR = '[data-testid="fto-pro-report-content"]';
/** FTO Pro 报告分享入口的稳定选择器。 */
const REPORT_SHARE_SELECTOR = '[data-testid="fto-pro-report-share"]';
/** FTO Pro 报告导出/下载入口的稳定选择器。 */
const REPORT_EXPORT_SELECTOR = '[data-testid="fto-pro-report-export"]';
/** 分享按钮作为 Driver 辅助目标时使用的高亮样式类。 */
const SECONDARY_HIGHLIGHT_CLASS = 'fto-pro-report-action-secondary-highlight';
/** 达到阅读阈值后，展示报告操作引导前的短暂停留时间。 */
const ACTION_GUIDE_DELAY_MS = 800;
/** 等待报告和操作目标渲染的最长时间。 */
const TARGET_WAIT_TIMEOUT_MS = 5000;
/** 开始报告操作引导所需的阅读进度。 */
const READING_PROGRESS_THRESHOLD = 0.2;


/** 当前 FTO 报告 onboarding 使用的身份和报告上下文。 */
interface OnboardingContext {
    /** 当前生成成功的报告 ID，仅用于生命周期去重。 */
    reportId: string;
    /** 当前浏览器用户的稳定身份。 */
    identityId: string;
}

/**
 * FTO Pro 报告分享/下载引导编排器。
 *
 * 领域成功事件由页面 composable 转交，service 只负责一次性状态、目标等待、
 * Driver 遮罩、双目标静态高亮和完整生命周期清理。
 */
@injectable()
export class FtoProReportOnboardingServiceImpl implements FtoProReportOnboardingService {
    /** 当前报告上下文。 */
    private _context?: OnboardingContext;
    /** 报告滚动容器。 */
    private _reportContentElement?: Element;
    /** 导出或下载入口。 */
    private _reportExportElement?: Element;
    /** 分享入口。 */
    private _reportShareElement?: Element;
    /** 当前报告操作引导 Driver。 */
    private _driver?: Driver;
    /** 分享按钮说明气泡的清理句柄。 */
    private _shareGuideBubbleDisposable?: Disposable;
    /** 达到阅读阈值后的操作引导停留检测定时器。 */
    private _actionGuideTimerId?: number;
    /** 当前用户是否已经展示过分享和下载引导。 */
    private _hasShownActionGuide = false;
    /** 当前报告操作引导是否可被按钮点击消费为归因。 */
    private _isActionGuideAttributionArmed = false;
    /** 报告内容监听是否已经绑定。 */
    private _areListenersBound = false;
    /** service 是否处于当前页面生命周期。 */
    private _isInitialized = false;
    /** 当前生命周期的报告资格是否已经完成判断。 */
    private _isEligibilityResolved = false;
    /** 当前生命周期的报告资格判断结果。 */
    private _isReportEligible = false;
    /** 当前生命周期是否正在请求报告资格。 */
    private _isEligibilityRequestPending = false;
    /** 资格判断期间最后收到的报告 ID。 */
    private _pendingReportId?: string;
    /** 本地调试是否已显式绕过公共资格门禁。 */
    private _isDebugMode = false;
    /** 当前正在处理的报告 ID，用于防止重复启动。 */
    private _startingReportId?: string;
    /** 当前生命周期中已经处理过的报告 ID。 */
    private _handledReportId?: string;
    /** 使销毁前的异步目标等待失效。 */
    private _lifecycleId = 0;

    // eslint-disable-next-line max-params
    constructor(
        /** 提供 DOM、Driver、定时器、身份和存储等浏览器能力。 */
        @inject(FtoProOnboardingRuntime)
        private readonly _runtime: FtoProOnboardingRuntimeType,
        /** 上报报告分享/下载引导展示埋点。 */
        @inject(RavenService)
        private readonly _ravenService: RavenServiceType,
        /** 独立判断并固化账号级报告 onboarding 资格。 */
        @inject(OnboardingEligibilityService)
        private readonly _onboardingEligibilityService: OnboardingEligibilityServiceType,
        /** 提供账号级报告引导后端状态。 */
        @inject(OnboardingStateService)
        private readonly _onboardingStateService: OnboardingStateServiceType,
        /** 提供报告操作说明的当前语言文案。 */
        @inject(I18nService)
        private readonly _i18nService: I18nServiceType,
        /** 为分享辅助目标展示独立说明气泡。 */
        @inject(ReportActionGuideBubblePresenter)
        private readonly _reportActionGuideBubblePresenter: ReportActionGuideBubblePresenterType,
    ) {}

    /** 初始化报告引导 service；重复调用不会改变生命周期。 */
    async initialize(): Promise<void> {
        if (this._isInitialized) return;
        this._isInitialized = true;
    }

    /** 接收真实报告成功事件，并异步启动一次报告引导。 */
    notifyReportGenerated(reportId: string): void {
        if (!this._isInitialized || !reportId) return;
        this._pendingReportId = reportId;
        if (!this._isEligibilityResolved) {
            this._resolveReportEligibility();
            return;
        }
        this._pendingReportId = undefined;
        if (this._isReportEligible) this._tryStartReport(reportId);
    }

    /** 在真实报告成功后解析一次独立报告资格，并继续处理期间缓存的事件。 */
    private _resolveReportEligibility(): void {
        if (this._isEligibilityRequestPending || this._isDebugMode) return;

        const lifecycleId = this._lifecycleId;
        this._isEligibilityRequestPending = true;
        void this._onboardingEligibilityService.isReportEligible().then((isEligible) => {
            if (!this._isCurrent(lifecycleId) || this._isDebugMode) return;

            this._isEligibilityResolved = true;
            this._isReportEligible = isEligible;
            const pendingReportId = this._pendingReportId;
            this._pendingReportId = undefined;
            if (isEligible && pendingReportId) this._tryStartReport(pendingReportId);
        }).catch((error) => {
            if (!this._isCurrent(lifecycleId) || this._isDebugMode) return;
            this._isEligibilityResolved = true;
            this._isReportEligible = false;
            this._pendingReportId = undefined;
            this._runtime.warn('Failed to resolve FTO Pro report onboarding eligibility:', error);
        }).finally(() => {
            if (this._isCurrent(lifecycleId)) this._isEligibilityRequestPending = false;
        });
    }

    /** 对已通过资格判断的报告启动一次引导。 */
    private _tryStartReport(reportId: string): void {
        if (this._startingReportId === reportId || this._handledReportId === reportId) return;

        const lifecycleId = this._lifecycleId;
        this._startingReportId = reportId;
        void this._start(reportId, lifecycleId)
            .then((started) => {
                if (this._isCurrent(lifecycleId) && started) this._handledReportId = reportId;
            })
            .catch((error) => {
                if (this._isCurrent(lifecycleId)) {
                    this._clearRuntimeState();
                    this._clearReportContext();
                }
                this._runtime.warn('Failed to start FTO Pro report onboarding:', error);
            })
            .finally(() => {
                if (this._startingReportId === reportId) this._startingReportId = undefined;
            });
    }

    /** 消费一次报告操作引导归因，并清理当前遮罩与静态辅助高亮。 */
    consumeActionGuide(): boolean {
        if (!this._isActionGuideAttributionArmed) return false;

        this._isActionGuideAttributionArmed = false;
        this._clearActionGuide();
        return true;
    }

    /** 仅在 Vite 开发环境绕过受众判断，强制启动一次报告视觉引导。 */
    debugStart(reportId: string): void {
        if (!import.meta.env.DEV || !reportId) return;
        if (!this._isInitialized) this._isInitialized = true;
        this._isDebugMode = true;
        this._isEligibilityResolved = true;
        this._isReportEligible = true;
        this.notifyReportGenerated(reportId);
    }

    /** 清理报告操作引导、监听器和全部异步资源。 */
    destroy(): void {
        this._isInitialized = false;
        this._isEligibilityResolved = false;
        this._isReportEligible = false;
        this._isEligibilityRequestPending = false;
        this._pendingReportId = undefined;
        this._isDebugMode = false;
        this._lifecycleId += 1;
        this._startingReportId = undefined;
        this._handledReportId = undefined;
        this._clearRuntimeState();
        this._clearReportContext();
    }

    /** 解析用户、目标和展示状态，启动当前尚未展示的阶段。 */
    // eslint-disable-next-line complexity
    private async _start(reportId: string, lifecycleId: number): Promise<boolean> {
        const identityId = await this._runtime.getIdentityId();
        if (!this._isCurrent(lifecycleId) || !identityId) return false;

        const onboardingState = this._isDebugMode
            ? {}
            : await this._onboardingStateService.getState(identityId);
        if (!this._isCurrent(lifecycleId)) return false;
        if (onboardingState.fto_report_actions_seen) {
            this._hasShownActionGuide = true;
            return true;
        }

        await this._runtime.waitForRender();
        if (!this._isCurrent(lifecycleId)) return false;

        const [reportContentElement, reportShareElement, reportExportElement] = await Promise.all([
            this._runtime.waitForElement(REPORT_CONTENT_SELECTOR, TARGET_WAIT_TIMEOUT_MS),
            this._runtime.waitForElement(REPORT_SHARE_SELECTOR, TARGET_WAIT_TIMEOUT_MS),
            this._runtime.waitForElement(REPORT_EXPORT_SELECTOR, TARGET_WAIT_TIMEOUT_MS),
        ]);
        if (
            !this._isCurrent(lifecycleId)
            || !reportContentElement
            || !reportShareElement
            || !reportExportElement
        ) return false;

        this._clearRuntimeState();
        this._context = { reportId, identityId };
        this._reportContentElement = reportContentElement;
        this._reportShareElement = reportShareElement;
        this._reportExportElement = reportExportElement;
        this._hasShownActionGuide = false;

        this._bindListeners();
        return true;
    }

    /** 绑定报告滚动和点击监听。 */
    private _bindListeners(): void {
        if (this._areListenersBound || !this._reportContentElement) return;
        this._runtime.addEventListener(this._reportContentElement, 'scroll', this._handleReportScroll);
        this._runtime.addEventListener(this._reportContentElement, 'click', this._handleReportClick);
        this._areListenersBound = true;
    }

    /** 移除报告滚动和点击监听。 */
    private _unbindListeners(): void {
        if (!this._areListenersBound || !this._reportContentElement) return;
        this._runtime.removeEventListener(this._reportContentElement, 'scroll', this._handleReportScroll);
        this._runtime.removeEventListener(this._reportContentElement, 'click', this._handleReportClick);
        this._areListenersBound = false;
    }

    /** 滚动达到阅读阈值后安排分享和下载按钮引导。 */
    private readonly _handleReportScroll: EventListener = () => {
        if (this._driver?.isActive()) {
            this._driver.refresh();
            return;
        }
        this._scheduleActionGuide();
    };

    /** 报告点击关闭当前激活的操作引导。 */
    private readonly _handleReportClick: EventListener = () => {
        if (this._driver?.isActive()) this._clearActionGuide();
    };

    /** 页面点击或非目标激活键盘交互都会结束分享和下载按钮引导。 */
    private readonly _handleDocumentInteraction: EventListener = (event) => {
        if (
            event instanceof KeyboardEvent
            && (event.key === 'Enter' || event.key === ' ')
            && this._isActionTarget(event.target)
        ) return;
        this._clearActionGuide();
    };

    /** 判断事件是否来自当前分享或下载目标。 */
    private _isActionTarget(target: EventTarget | null): boolean {
        if (!(target instanceof Node)) return false;
        return Boolean(
            this._reportShareElement?.contains(target)
            || this._reportExportElement?.contains(target),
        );
    }

    /** 阅读进度达到 20% 且停留 800ms 后展示报告操作引导。 */
    private _scheduleActionGuide(): void {
        if (this._hasShownActionGuide || !this._reportContentElement) return;

        const progress = this._runtime.getReadingProgress(this._reportContentElement);
        if (progress < READING_PROGRESS_THRESHOLD) {
            this._clearActionGuideTimer();
            return;
        }

        this._clearActionGuideTimer();
        const lifecycleId = this._lifecycleId;
        this._actionGuideTimerId = this._runtime.setTimeout(() => {
            this._actionGuideTimerId = undefined;
            if (!this._isCurrent(lifecycleId) || !this._reportContentElement) return;
            if (this._runtime.getReadingProgress(this._reportContentElement) < READING_PROGRESS_THRESHOLD) return;
            this._showActionGuide();
        }, ACTION_GUIDE_DELAY_MS);
    }

    /** 创建 Driver 遮罩，以导出为主目标并静态提升分享按钮。 */
    // eslint-disable-next-line complexity
    private _showActionGuide(): void {
        const context = this._context;
        const shareElement = this._reportShareElement;
        const exportElement = this._reportExportElement;
        if (!context || !shareElement || !exportElement || this._hasShownActionGuide) return;
        if (!this._runtime.isElementEnabled(shareElement) || !this._runtime.isElementEnabled(exportElement)) return;

        try {
            const driverInstance = this._runtime.createDriver(this._createDriverConfig());
            this._driver = driverInstance;
            this._runtime.addClass(shareElement, SECONDARY_HIGHLIGHT_CLASS);
            registerOnboardingLocaleMessages(this._i18nService, messages);
            driverInstance.highlight(createReportActionGuideStep(
                exportElement,
                this._i18nService.t('onboarding.reportActions.exportDescription'),
            ));
            if (!driverInstance.isActive()) {
                this._clearActionGuide(false);
                return;
            }
            this._shareGuideBubbleDisposable = this._reportActionGuideBubblePresenter.show({
                target: shareElement,
                description: this._i18nService.t('onboarding.reportActions.shareDescription'),
            });

            this._isActionGuideAttributionArmed = true;
            this._hasShownActionGuide = true;
            void this._onboardingStateService.updateState(
                context.identityId,
                { fto_report_actions_seen: true },
                'optimistic',
            ).catch(error => this._runtime.warn(
                'Failed to persist FTO Pro report onboarding state:',
                error,
            ));
            this._runtime.addDocumentEventListener('click', this._handleDocumentInteraction);
            this._runtime.addDocumentEventListener('keydown', this._handleDocumentInteraction);
            this._track(TRACK_EVENT_AGENT_REPORT_ACTIONS_SPOTLIGHTED);
        } catch (error) {
            this._clearActionGuide();
            this._runtime.warn('Failed to show FTO Pro report action guide:', error);
        }
    }

    /** 创建以导出按钮为主目标的 Driver 配置。 */
    private _createDriverConfig(): Config {
        return {
            animate: true,
            overlayColor: ACTION_GUIDE_OVERLAY_COLOR,
            overlayOpacity: ACTION_GUIDE_OVERLAY_OPACITY,
            allowClose: true,
            allowScroll: true,
            overlayClickBehavior: () => this._clearActionGuide(),
            disableActiveInteraction: false,
            allowKeyboardControl: true,
            stagePadding: 4,
            stageRadius: 10,
            onDestroyed: (_element, _step, { driver: destroyedDriver }) => {
                if (this._driver !== destroyedDriver) return;
                this._clearActionGuide(false);
            },
        };
    }

    /** 清理报告操作引导、文档监听、静态辅助高亮和 Driver。 */
    private _clearActionGuide(destroyDriver = true): void {
        this._isActionGuideAttributionArmed = false;
        this._clearActionGuideTimer();
        this._runtime.removeDocumentEventListener('click', this._handleDocumentInteraction);
        this._runtime.removeDocumentEventListener('keydown', this._handleDocumentInteraction);
        this._shareGuideBubbleDisposable?.dispose();
        this._shareGuideBubbleDisposable = undefined;
        if (this._reportShareElement) {
            this._runtime.removeClass(this._reportShareElement, SECONDARY_HIGHLIGHT_CLASS);
        }

        const driverInstance = this._driver;
        this._driver = undefined;
        if (destroyDriver && driverInstance?.isActive()) driverInstance.destroy();
    }

    /** 清理当前页面所有可见 UI、监听器和定时器。 */
    private _clearRuntimeState(): void {
        this._clearActionGuide();
        this._unbindListeners();
    }

    /** 清空报告目标和当前阶段状态。 */
    private _clearReportContext(): void {
        this._context = undefined;
        this._reportContentElement = undefined;
        this._reportShareElement = undefined;
        this._reportExportElement = undefined;
        this._hasShownActionGuide = false;
    }

    /** 清理报告操作引导延迟计时器。 */
    private _clearActionGuideTimer(): void {
        if (this._actionGuideTimerId === undefined) return;
        this._runtime.clearTimeout(this._actionGuideTimerId);
        this._actionGuideTimerId = undefined;
    }

    /** 判断异步回调是否仍属于当前有效生命周期。 */
    private _isCurrent(lifecycleId: number): boolean {
        return this._isInitialized && this._lifecycleId === lifecycleId;
    }

    /** 上报 FTO onboarding 埋点，仅附带当前事件中会变化的明细。 */
    private _track(eventCode: string, detail?: Record<string, string>): void {
        void this._ravenService.track(eventCode, {
            eventType: TRACK_EVENT_TYPE.FtoReport,
            sourceType: eventCode,
            ...(detail ? { eventDetailStr: detail } : {}),
        }).catch(() => undefined);
    }
}
